import {useEffect, useRef, useState} from "react";
import type {Image} from "@/images/Image.ts";
import {createEmptyLevelGeometry, type LevelGeometryDocument} from "@/dungeons/model/DungeonGeometry.ts";
import useLevelGeometryDocument, {type GeometryDocumentUpdate} from "./useLevelGeometryDocument.ts";
import {useDungeonEditorSession} from "./DungeonEditorSession.tsx";
import DungeonEditorWorkspace from "./DungeonEditorWorkspace.tsx";
import {activeEditorTool, availableEditorTools, type UnifiedEditorTool} from "./UnifiedEditorTools.ts";
import UnifiedEditorSidebar from "./UnifiedEditorSidebar.tsx";
import UnifiedEditorToolbar from "./UnifiedEditorToolbar.tsx";
import UnifiedDungeonCanvas from "./UnifiedDungeonCanvas.tsx";
import useGridCalibration from "@/dungeons/editor/grid/useGridCalibration.ts";
import useGeometryEditorController from "@/dungeons/editor/geometry/useGeometryEditorController.ts";
import useDungeonAnnotations from "@/dungeons/editor/annotations/useDungeonAnnotations.ts";
import useDungeonLights from "@/dungeons/editor/lighting/useDungeonLights.ts";
import useLevelLighting from "@/dungeons/editor/lighting/useLevelLighting.ts";

const EMPTY_GEOMETRY = createEmptyLevelGeometry();

export default function DungeonUnifiedEditor() {
    const {activeLevel, levels, viewport, setViewport, snappingEnabled, setSnappingEnabled, lightingPreview} = useDungeonEditorSession();
    const geometryDocument = useLevelGeometryDocument({
        levelId: activeLevel?.id ?? "",
        sourceRevision: activeLevel?.geometryRevision ?? 0,
        document: activeLevel?.geometry ?? EMPTY_GEOMETRY,
    });
    if (!activeLevel) return null;
    return <LevelWorkspace key={geometryDocument.sourceKey} document={geometryDocument.document}
        image={activeLevel.map?.image} levelId={activeLevel.id} levels={levels}
        updateDocument={geometryDocument.updateDocument}
        lightingPreview={lightingPreview}
        viewport={viewport} setViewport={setViewport} snappingEnabled={snappingEnabled}
        setSnappingEnabled={setSnappingEnabled}/>;
}

function LevelWorkspace({document, image, levelId, levels, updateDocument, viewport, setViewport, lightingPreview,
    snappingEnabled, setSnappingEnabled}: {
    document: LevelGeometryDocument;
    lightingPreview: boolean;
    image?: Image;
    levelId: string;
    levels: ReturnType<typeof useDungeonEditorSession>["levels"];
    updateDocument: (update: GeometryDocumentUpdate) => void;
    viewport: ReturnType<typeof useDungeonEditorSession>["viewport"];
    setViewport: ReturnType<typeof useDungeonEditorSession>["setViewport"];
    snappingEnabled: boolean;
    setSnappingEnabled: (enabled: boolean) => void;
}) {
    const [requestedTool, setRequestedTool] = useState<UnifiedEditorTool>(
        document.registration.calibrated ? "select" : "calibrate");
    const tool = activeEditorTool(requestedTool, document);
    const grid = useGridCalibration({document, updateDocument, viewport, setViewport});
    const geometry = useGeometryEditorController({document, updateDocument});
    const annotations = useDungeonAnnotations({document, updateDocument, setViewport});
    const lights = useDungeonLights({document, updateDocument});
    const levelLighting = useLevelLighting({document, updateDocument});
    const transitionTargetLevel = levels.find(level => level.id === geometry.selectedTransition?.targetLevelId);
    const wasCalibrated = useRef(document.registration.calibrated);

    useEffect(() => {
        if (wasCalibrated.current && !document.registration.calibrated) {
            geometry.commands.selectTool("select");
            grid.reset();
            annotations.close();
            lights.select(undefined);
            setRequestedTool("calibrate");
        }
        wasCalibrated.current = document.registration.calibrated;
    }, [document.registration.calibrated, geometry.commands, grid, annotations, lights]);

    const selectTool = (next: UnifiedEditorTool, preserveInteraction = false) => {
        if (!availableEditorTools(document).includes(next)) return;
        if (preserveInteraction) {
            setRequestedTool(next);
            return;
        }
        if (next === tool) return;
        geometry.commands.selectTool(next === "wall" || next === "door" || next === "transition" ? next : "select");
        grid.reset();
        annotations.close();
        if (next !== "select" && next !== "light") lights.select(undefined);
        setRequestedTool(next);
    };

    return <DungeonEditorWorkspace sidebar={<UnifiedEditorSidebar tool={tool} document={document}
        sourceLevelId={levelId} levels={levels}
        hasImage={Boolean(image)} grid={grid} geometry={geometry}
        annotations={annotations} lights={lights} levelLighting={levelLighting} snappingEnabled={snappingEnabled}
        onSnappingEnabledChange={setSnappingEnabled} onCalibrationApplied={() => setRequestedTool("select")}
        onFinishTransition={() => {
            geometry.commands.finishTransition();
            selectTool("select", true);
        }}/> }>
        <div className="relative size-full">
            {image ? <UnifiedDungeonCanvas image={image} document={document} viewport={viewport}
                setViewport={setViewport} tool={tool} onSelectTool={selectTool} grid={grid}
                transitionTargetGeometry={transitionTargetLevel?.geometry}
                transitionTargetName={transitionTargetLevel?.name}
                geometry={geometry} annotations={annotations} lights={lights} snappingEnabled={snappingEnabled}
                lightingPreview={lightingPreview} directionalLight={levelLighting.light}/> :
                <div className="grid size-full place-content-center text-sm text-muted-foreground">
                    Für dieses Level muss zuerst eine Karte hochgeladen werden.
                </div>}
            {image && <UnifiedEditorToolbar tool={tool} document={document} onSelectTool={selectTool}/>}
        </div>
    </DungeonEditorWorkspace>;
}
